<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import RegionGoogleMap from '@/components/maps_canvases/RegionGoogleMap.vue';
import RegionDataTable from '@/components/tables/RegionDatatable.vue';
import CanvasDataPanels from '@/components/utilities/CanvasDataPanels.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import { InlineSearchBar } from '@/components/utilities/search';

import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useRegionsApi } from '@/composables/useRegionsApi.ts';

import  { type CMSListingTypeEnum, ListingTypeEnum, listingTypeEnumNumericValueFromCMS } from '@/enums/ListingType.ts';
import type { AvailableLocales, LocaleSchema } from '@/locales/index.ts';
import type { Region } from '@/typings/regions/Region.ts';
import type { GetRegionsRequest } from '@/typings/requests/GetRegionsRequest.ts';

const { t, locale } = useI18n<{message: LocaleSchema}, AvailableLocales>({
  useScope: 'global',
});

const { getAllRegions, getAllRegionsLoading } = useRegionsApi();

const { getRegionWizardUrl } = usePurchaseLocalizedUrls();

const props = defineProps<{
  unitOccupancyType: CMSListingTypeEnum;
}>();

const hoveredRegion = ref<Region>();
const mapIsLoaded = ref(false);
const wizardStep = ref(1);

const regions = ref<Region[]>([]);



const mapIsReady = computed(() => {
  return regions.value.length > 0 && mapIsLoaded.value;
});

/* Event handlers */
function onMapLoaded() {
  mapIsLoaded.value = true;
}

function onRegionSelected(region: Region) {
  // Navigation directe vers l'étape suivante
  window.location.href = getRegionWizardUrl(region);
}

onMounted(async () => {
  const request: GetRegionsRequest  = {
    locale: locale.value.substring(0, 2),
    listingType: listingTypeEnumNumericValueFromCMS[props.unitOccupancyType]
  };

  regions.value = await getAllRegions(request);
});

</script>

<template>
  <!--Achat - Étape 1 - Choix de la ville-->
  <div class="region-purchase-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar :auto-save="false" :listing-type="ListingTypeEnum.Sale" :units-search="true" />
    <div class="wrapper wrapper_large">
      <UnitSelectionWizardSteps v-model="wizardStep" />
    
      <div class="main-content">
        <!--En-tête avec bouton de retour-->
        <HeaderWithBackButton :title="t('browsingApp.regions.chooseTheRegionDescription')"
                              :hide-back-button="true" />
        <CanvasDataPanels>
          <template v-if="!getAllRegionsLoading"
                    #canvas>
            <template v-if="regions?.length">
              <RegionGoogleMap v-show="mapIsReady"
                               v-model:hovered-model="hoveredRegion"
                               :regions="regions"
                               @map-loaded="onMapLoaded"
                               @click-marker="onRegionSelected" />
              <LoadingSpinner v-show="!mapIsReady" />
            </template>
            <template v-else>
              <div class="placeholderNoInformation">
                {{ t('browsingApp.regions.noMapDataFound') }}   
              </div>
            </template>
          </template>
          <template v-if="!getAllRegionsLoading"
                    #data>
            <template v-if="regions?.length">
              <RegionDataTable v-model:hovered="hoveredRegion"                         
                               :regions="regions"
                               @row-action="onRegionSelected" />
            </template>
            <template v-else>
              <div class="placeholderNoInformation">
                {{ t('browsingApp.regions.noRegionFound') }}   
              </div>
            </template>
          </template>
        </CanvasDataPanels>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.region-purchase-browsing{
  padding-top:91.19px;
}
@media screen and (max-width: 1024px) {
	.region-purchase-browsing {
		padding-top: 66px;
	}
}
</style>